import { Box, Button, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import SingleDatePicker from '../../../components/form/SingleDatePicker';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ProfileCard from '../../common/ProfileCard';
import ItemDetailsTable from '../../common/ItemDetailsTable';
import pricingEngineForDocuments, {
  buildSubmitLineItemPrice,
  normalizeLineItemPrice,
} from '../../../utils/pricingEngineForDocuments';
import AddLineItemButton from '../../../components/form/AddLineItemButton';
import BusinessProfileSelect from '../../../components/form/BusinessProfileSelect';
import CustomerSelect from '../../../components/form/CustomerSelect';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import TopBar from '../../../components/TopBar';
import PaymentMeansFields from '../../../components/form/PaymentMeansFields';
import TransactionTypeFields from '../../../components/form/TransactionTypeFields';
import DocumentTypeSelect from '../../../components/form/DocumentTypeSelect';
import { applyApiErrors } from '../../../utils/apiFieldErrors';
import DocumentCurrencyInput from '../../../components/form/DocumentCurrencyInput';
import { getInvoiceValidationSchema } from '../../../validations/InvoiceValidation';
import CustomTextInput from '../../../components/CustomTextInput';
import DocumentSummarySection from '../../../components/DocumentSummarySection';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import CopyTextComponent from '../../../components/CopyTextComponent';
import { useSelector } from 'react-redux';
import DocumentLevelChargeAndAllowancesButton from '../../common/DocumentLevelChargeAndAllowancesButton';
import { transactionTypePositionCheck, getAllFilesTotalSize } from '../../../utils/helper';
import { buildInvoicePeriodPayload } from '../../../utils/invoicePeriodPayload';
import { getCountryOptionByCode, getSubdivisionOptionByCode } from '../../../api/codelist/codeListsSlice';
import {
  UAE_COUNTRY_CODE,
  DEFAULT_CURRENCY_CODE,
  DEFAULT_DOCUMENT_SOURCE,
  DEFAULT_TRANSITION_TYPE_CODE,
  INVOICE_DOCUMENT_TYPE_CODE,
} from '../../../constants/defaults';
import { useAutoPopulateForm } from '../../../hooks/useAutoPopulateForm';
import { useNavigate } from 'react-router-dom';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import MultipleFileUpload from '../../Invoices/components/MultipleFileUpload';
import AttachmentViewer from '../../Invoices/components/AttachmentViewer';
import { formatFileSize } from '../../../utils/formatFileSize';
import { nowInGulfTimeString } from '../../../utils/formatDate';
import DocumentNoteField from '../../common/DocumentNoteField';

export default function SalesInvoiceRegisterForm({
  initialData = {},
  onSubmit,
  onCancel,
  readOnly = false,
  apiErrors = {},
  isLoading = false,
}) {
  const { eInvoiceForm, validations, language, salesInvoiceRegister, common } = useTranslatedMessages();
  const formMessages = salesInvoiceRegister?.form ?? {};
  const validationSchema = useMemo(() => getInvoiceValidationSchema(validations, { isPurchase: false }), [validations]);

  const MAX_TOTAL_SIZE = 6 * 1024 * 1024;
  const { page, top_bar, buttons, invoice_form, cards, document_note } = eInvoiceForm;
  const form = invoice_form;
  const { settings } = useSelector((state) => state.org.org);
  const [profilesData, setProfilesData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [rawFiles, setRawFiles] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const navigate = useNavigate();

  const backAction = () => {
    if (readOnly) {
      navigate(-1);
    } else {
      onCancel();
    }
  };

  const paymentMeansData = Array.isArray(initialData?.payment_means)
    ? initialData.payment_means
    : initialData?.payment_means
      ? [initialData.payment_means]
      : [];
  const countryOption = useSelector(
    getCountryOptionByCode(initialData.delivery?.delivery_location?.address?.country_code),
  );
  const subdivisionOption = useSelector(
    getSubdivisionOptionByCode(initialData.delivery?.delivery_location?.address?.country_subentity),
  );
  const {
    control,
    getValues,
    setValue,
    handleSubmit,
    setError,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: yupResolver(validationSchema),
    context: {
      auto_numbering_enabled: settings?.auto_numbering_enabled ?? false,
    },
    mode: 'onChange',
    defaultValues: {
      invoice_type_code: initialData?.invoice_type_code ?? INVOICE_DOCUMENT_TYPE_CODE,
      document_source: DEFAULT_DOCUMENT_SOURCE,
      profile_execution_id: initialData?.profile_execution_id || DEFAULT_TRANSITION_TYPE_CODE,
      document_number: initialData?.document_number || '',
      issue_date: dayjs(initialData?.issue_date) || null,
      due_date: initialData?.due_date ? dayjs(initialData.due_date) : null,
      issue_time: nowInGulfTimeString(),
      supply_date: initialData?.delivery?.actual_delivery_date
        ? dayjs(initialData.delivery.actual_delivery_date)
        : initialData?.tax_point_date
          ? dayjs(initialData.tax_point_date)
          : null,
      payment_means: paymentMeansData,
      document_lines: (initialData?.document_lines ?? []).map((item, index) => ({
        ...item,
        key: item?.key || `ITM-${index}`,
        ...(item.price && { price: normalizeLineItemPrice(item.price) }),
      })),
      document_currency_code: initialData?.document_currency_code || 'AED',
      tax_exchange_rate: initialData?.tax_exchange_rate || null,
      accounting_supplier_party: initialData?.accounting_supplier_party || null,
      accounting_customer_party: initialData?.accounting_customer_party || null,
      charges: initialData?.charges || [],
      allowances: initialData?.allowances || [],
      invoice_period_start_date: initialData?.invoice_period?.start_date
        ? dayjs(initialData.invoice_period.start_date)
        : null,
      invoice_period_end_date: initialData?.invoice_period?.end_date
        ? dayjs(initialData.invoice_period.end_date)
        : null,
      invoice_period_description: initialData?.invoice_period?.description || null,

      principle_id_trn: initialData?.seller_supplier_party?.id || null,
      beneficiary_id: initialData?.buyer_customer_party?.id || null,
      delivery_party_name: initialData?.delivery?.party_name || null,
      incoterms: initialData?.delivery?.terms || null,
      delivery_street_name: initialData?.delivery?.delivery_location?.address?.street_name || null,
      delivery_city_name: initialData?.delivery?.delivery_location?.address?.city_name || null,
      delivery_country_code: initialData?.delivery?.delivery_location?.address?.country_code ? countryOption : null,
      delivery_country_subentity:
        countryOption?.code === UAE_COUNTRY_CODE
          ? subdivisionOption
          : initialData.delivery?.delivery_location?.address?.country_subentity,
      delivery_postal_zone: initialData?.delivery?.delivery_location?.address?.postal_zone || null,
    },
  });

  useEffect(() => {
    applyApiErrors(setError, apiErrors);
  }, [setError, apiErrors]);

  const {
    fields: lineItems,
    replace: replaceLineItems,
    append: appendLineItem,
    remove: removeLineItem,
  } = useFieldArray({ control, name: 'document_lines' });

  const handleSetBilledTo = (data) => {
    setValue('accounting_customer_party', data);
  };

  const invoiceTypeCode = useWatch({ control, name: 'invoice_type_code' });
  const profileExecutionId = useWatch({ control, name: 'profile_execution_id' });
  const previousInvoiceTypeCodeRef = useRef(invoiceTypeCode);

  useAutoPopulateForm({
    profiles: profilesData,
    customers: customerData,
    suppliers: null,
    documentType: 'SALES',
    setValue,
    getValues,
    invoiceTypeCode,
  });

  const billedBy = useWatch({ control, name: 'accounting_supplier_party' });
  const billedTo = useWatch({ control, name: 'accounting_customer_party' });
  const document_lines = useWatch({ control, name: 'document_lines' }) || [];
  const docCharges = useWatch({ control, name: 'charges' }) || [];
  const docAllowances = useWatch({ control, name: 'allowances' }) || [];
  const watchedPaymentMeans = useWatch({ control, name: 'payment_means' });
  const conversionRate = useWatch({ control, name: 'tax_exchange_rate.calculation_rate' });
  const documentDate = useWatch({ control, name: 'issue_date' });

  useEffect(() => {
    if (Object.keys(errors || {}).length) {
      trigger();
    }
    trigger('due_date');
  }, [language, documentDate, trigger]);

  useEffect(() => {
    trigger(['document_lines', 'allowances', 'charges']);
  }, [profileExecutionId, trigger]);

  useEffect(() => {
    const previousInvoiceTypeCode = previousInvoiceTypeCodeRef.current;
    if (!previousInvoiceTypeCode || previousInvoiceTypeCode === invoiceTypeCode) {
      previousInvoiceTypeCodeRef.current = invoiceTypeCode;
      return;
    }

    setValue('accounting_supplier_party', null, { shouldValidate: true });
    setValue('profile_execution_id', DEFAULT_TRANSITION_TYPE_CODE, { shouldValidate: true });

    const resetDocumentLines = (getValues('document_lines') || []).map((line) => ({
      ...line,
      classified_tax_category: {
        id: null,
        percent: 0,
        tax_exemption_reason_code: null,
        tax_exemption_reason: null,
        tax_scheme: line?.classified_tax_category?.tax_scheme || 'VAT',
      },
    }));
    setValue('document_lines', resetDocumentLines, { shouldValidate: true });

    const resetDocLevelTaxCategory = (records) =>
      (records || []).map((record) => ({
        ...record,
        tax_category: {
          id: null,
          percent: 0,
          tax_exemption_reason_code: null,
          tax_exemption_reason: null,
          tax_scheme: record?.tax_category?.tax_scheme || 'VAT',
        },
      }));

    setValue('allowances', resetDocLevelTaxCategory(getValues('allowances')), { shouldValidate: true });
    setValue('charges', resetDocLevelTaxCategory(getValues('charges')), { shouldValidate: true });
    trigger(['accounting_supplier_party', 'profile_execution_id', 'document_lines', 'allowances', 'charges']);

    previousInvoiceTypeCodeRef.current = invoiceTypeCode;
  }, [getValues, invoiceTypeCode, setValue, trigger]);

  const {
    itemsSubtotal,
    subtotal,
    totalTaxAmount,
    taxInAED,
    totalItemAllowances,
    totalExcludingTax,
    totalIncludingTax,
    totalItemCharges,
    totalPayableAmount,
    totalTaxableAmount,
    totalNonTaxableAmount,
    taxBreakdown,
    headerAllowance,
    headerCharges,
    payableRoundingAmount,
  } = pricingEngineForDocuments({
    items: document_lines,
    docCharges,
    docAllowances,
    conversionRatePerAED: conversionRate,
    ...(readOnly &&
      initialData?.payable_rounding_amount != null && {
        payableRoundingAmount: initialData.payable_rounding_amount,
      }),
  });

  useEffect(() => {
    setValue('_total_payable', totalPayableAmount, { shouldValidate: false });
    trigger('due_date');
  }, [totalPayableAmount, profileExecutionId, setValue, trigger]);

  const handleFormSubmit = async (data) => {
    const transformedDocumentLines = data.document_lines?.map((line) => ({
      ...line,
      price: buildSubmitLineItemPrice(line.price),
    }));
    const shouldSendDeliveryData =
      transactionTypePositionCheck(data.profile_execution_id, 7) ||
      transactionTypePositionCheck(data.profile_execution_id, 8);

    const shouldSendInvoicePeriodData = transactionTypePositionCheck(data.profile_execution_id, 4);
    const shouldSendPrincipleIdTrnData = transactionTypePositionCheck(data.profile_execution_id, 6);
    const shouldSendBeneficiaryId = transactionTypePositionCheck(data.profile_execution_id, 1);

    const actualDeliveryDate =
      data.supply_date && dayjs(data.supply_date).isValid() ? dayjs(data.supply_date).format('YYYY-MM-DD') : null;
    const { supply_date: _supplyDate, ...formData } = data;

    const payload = {
      ...formData,
      document_lines: transformedDocumentLines,
      due_date: data.due_date ? dayjs(data.due_date).format('YYYY-MM-DD') : null,
      issue_date: dayjs(data.issue_date).format('YYYY-MM-DD'),
      issue_time: nowInGulfTimeString(),
      sub_total: subtotal,
      total_excluding_tax: totalExcludingTax,
      total_tax: totalTaxAmount,
      total_including_tax: totalIncludingTax,
      total_payable: totalPayableAmount,
      header_allowance: headerAllowance,
      header_charges: headerCharges,
      payment_means: data.payment_means,
      total_paid: 0,
      ...(data?.document_currency_code !== DEFAULT_CURRENCY_CODE && {
        tax_currency_code: DEFAULT_CURRENCY_CODE,
      }),
      ...(attachments?.length && { attachments: [...attachments] }),
      ...(shouldSendPrincipleIdTrnData && {
        seller_supplier_party: { id: data?.principle_id_trn },
      }),
      ...(shouldSendBeneficiaryId && {
        buyer_customer_party: { id: data?.beneficiary_id },
      }),
      ...(shouldSendInvoicePeriodData && {
        invoice_period: buildInvoicePeriodPayload(data),
      }),
      ...(shouldSendDeliveryData && {
        delivery: {
          ...(actualDeliveryDate && { actual_delivery_date: actualDeliveryDate }),
          party_name: data?.delivery_party_name,
          terms: data?.incoterms,
          delivery_location: {
            address: {
              street_name: data?.delivery_street_name,
              city_name: data?.delivery_city_name,
              country_code: data?.delivery_country_code?.code,
              country: data?.delivery_country_code?.label,
              country_subentity:
                data?.delivery_country_code?.code === UAE_COUNTRY_CODE
                  ? data?.delivery_country_subentity?.code
                  : data?.delivery_country_subentity,
              postal_zone: data?.delivery_postal_zone,
            },
          },
        },
      }),
      ...(!shouldSendDeliveryData &&
        actualDeliveryDate && {
          delivery: {
            actual_delivery_date: actualDeliveryDate,
          },
        }),
    };

    onSubmit(payload);
  };

  const openEInvoice = () => {
    const documentId = initialData?.uuid || initialData?.id;
    if (documentId) {
      navigate(`/sales/documents/invoices/${documentId}`);
    }
  };

  return (
    <>
      <Box className="InvoiceDetails" sx={{ pt: 2, pb: 4 }}>
        <Grid container sx={{ display: 'block' }}>
          <Grid>
            <TopBar
              title={
                <Stack direction="row" gap={3}>
                  {readOnly ? (
                    <>
                      {formMessages.register_details ?? top_bar.invoice_details}{' '}
                      <CopyTextComponent copyText={initialData.id} />
                    </>
                  ) : (
                    page.title_register_sales
                  )}
                </Stack>
              }
            >
              <Stack direction="row" spacing={1}>
                {readOnly && (
                  <Button
                    data-testid="refresh-button"
                    variant="outlined"
                    color="primary"
                    onClick={() => window.location.reload()}
                  >
                    <RefreshIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                    Refresh
                  </Button>
                )}
                {readOnly && initialData?.document_mode === 'E_INVOICE' && (
                  <Button variant="outlined" color="primary" onClick={openEInvoice}>
                    {formMessages.view_e_invoice_link ?? 'Open E-Invoice'}
                  </Button>
                )}
                <Button
                  data-testid="cancel-button"
                  variant={readOnly ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={backAction}
                  disabled={isLoading}
                >
                  {readOnly ? buttons.back : buttons.cancel}
                </Button>
                {!readOnly && (
                  <Button
                    data-testid="submit-button"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit(handleFormSubmit)}
                    disabled={isLoading}
                  >
                    {buttons.submit}
                  </Button>
                )}
              </Stack>
            </TopBar>
          </Grid>
        </Grid>
      </Box>

      <Box className="InvoiceDetails">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <CustomTextInput
              data-testid="document-number-input"
              label={form.invoice_number_label}
              control={control}
              errors={errors}
              name="document_number"
              readOnly={readOnly}
              disabled={settings?.auto_numbering_enabled}
              helperText={settings?.auto_numbering_enabled ? form.auto_numbering_helper : ''}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SingleDatePicker
              data-testid="invoice-date-input"
              name="issue_date"
              label={form.invoice_issue_date_label}
              control={control}
              errors={errors}
              readOnly={readOnly}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SingleDatePicker
              data-testid="invoice-delivery-date-input"
              name="supply_date"
              label={form.supply_date_label}
              control={control}
              errors={errors}
              readOnly={readOnly}
              clearable
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SingleDatePicker
              data-testid="invoice-due-date-input"
              name="due_date"
              label={form.due_date_label}
              control={control}
              errors={errors}
              readOnly={readOnly}
              clearable
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} mt={2}>
          <PaymentMeansFields
            control={control}
            errors={errors}
            readOnly={readOnly}
            watchedPaymentMeans={watchedPaymentMeans}
            setValue={setValue}
          />
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <DocumentTypeSelect
              data-testid="invoice-type-select"
              control={control}
              errors={errors}
              readOnly={readOnly}
              isPurchase={false}
              documentType="invoice"
              name="invoice_type_code"
              label={common.document_type_select.invoice_type_label}
            />
          </Grid>
          <TransactionTypeFields
            control={control}
            errors={errors}
            readOnly={readOnly}
            profileExecutionId={profileExecutionId}
            invoiceTypeCode={invoiceTypeCode}
            setValue={setValue}
            trigger={trigger}
          />
        </Grid>
        <Grid container spacing={2} mt={2} alignItems="stretch">
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', flexDirection: 'column' }}>
            <BusinessProfileSelect
              data-testid="billed-by-select"
              label={cards.billed_by_label}
              name="accounting_supplier_party"
              control={control}
              errors={errors}
              readOnly={readOnly}
              invoiceTypeCode={invoiceTypeCode}
              onProfilesLoaded={setProfilesData}
            />
            {billedBy && (
              <Box sx={{ mt: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <ProfileCard
                  title={cards.billed_by_title}
                  subtitle={cards.billed_by_subtitle}
                  document={{ ...billedBy }}
                />
              </Box>
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', flexDirection: 'column' }}>
            <CustomerSelect
              data-testid="billed-to-select"
              label={cards.billed_to_label}
              name="accounting_customer_party"
              control={control}
              errors={errors}
              readOnly={readOnly}
              onSelect={handleSetBilledTo}
              setError={setError}
              onCustomerLoaded={setCustomerData}
            />
            {billedTo && (
              <Box sx={{ mt: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <ProfileCard
                  title={cards.billed_to_title}
                  subtitle={cards.billed_to_subtitle}
                  rawData={billedTo}
                  readOnly={readOnly}
                  onUpdate={handleSetBilledTo}
                  document={{ ...billedTo }}
                />
              </Box>
            )}
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12 }}>
            <Grid container sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Grid>
                {!readOnly && (
                  <AddLineItemButton
                    errors={errors}
                    addItem={appendLineItem}
                    lineItems={lineItems}
                    invoiceTypeCode={invoiceTypeCode}
                    profileExecutionId={profileExecutionId}
                  />
                )}
              </Grid>
              <Grid container spacing={2} direction="row" alignItems="center" marginBottom="1.2em">
                <DocumentCurrencyInput
                  control={control}
                  errors={errors}
                  documentCurrencyCode={useWatch({ control, name: 'document_currency_code' })}
                  readOnly={readOnly}
                  setValue={setValue}
                />
              </Grid>
            </Grid>
            <ItemDetailsTable
              items={lineItems}
              remove={removeLineItem}
              replace={replaceLineItems}
              add={appendLineItem}
              invoiceTypeCode={invoiceTypeCode}
              profileExecutionId={profileExecutionId}
              errors={errors}
              readOnly={readOnly}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={{ md: 6 }}>
            <DocumentLevelChargeAndAllowancesButton
              control={control}
              readOnly={readOnly}
              currentNetAmount={totalExcludingTax}
              itemBaseAmount={totalExcludingTax}
              invoiceTypeCode={invoiceTypeCode}
              profileExecutionId={profileExecutionId}
              errors={errors}
            />
          </Grid>
          <Grid size={{ md: 6 }}>
            <DocumentSummarySection
              itemsSubtotal={itemsSubtotal}
              subtotal={subtotal}
              totalItemAllowances={totalItemAllowances}
              totalItemCharges={totalItemCharges}
              totalExcludingTax={totalExcludingTax}
              totalIncludingTax={totalIncludingTax}
              totalTaxAmount={totalTaxAmount}
              taxInAED={taxInAED}
              totalPayableAmount={totalPayableAmount}
              documentCurrencyCode={getValues('document_currency_code')}
              headerCharges={headerCharges}
              headerAllowance={headerAllowance}
              totalTaxableAmount={totalTaxableAmount}
              totalNonTaxableAmount={totalNonTaxableAmount}
              taxBreakdown={taxBreakdown}
              payableRoundingAmount={payableRoundingAmount}
            />
          </Grid>
        </Grid>

        <DocumentNoteField
          control={control}
          errors={errors}
          readOnly={readOnly}
          value={initialData?.note}
          messages={document_note}
        />

        {!readOnly && (
          <Grid container spacing={2} sx={{ my: 2 }}>
            <Grid size={{ md: 6 }}>
              <Typography variant="h6" fontWeight="light-bold">
                {formMessages.supporting_documents ?? 'Supporting Documents'}
              </Typography>
              <Typography variant="body2" fontWeight="light">
                {formMessages.supporting_documents_hint ?? 'Attach reference files'} (
                {formatFileSize(getAllFilesTotalSize(rawFiles))} of {formatFileSize(MAX_TOTAL_SIZE)} used)
              </Typography>
            </Grid>
            <Grid size={12}>
              <MultipleFileUpload
                onUpload={setAttachments}
                onRawFileUpload={setRawFiles}
                maxTotalSize={MAX_TOTAL_SIZE}
              />
            </Grid>
          </Grid>
        )}

        {readOnly && initialData?.attachments && (
          <AttachmentViewer
            attachments={initialData.attachments}
            docId={initialData.id}
            isPurchase={false}
            isCreditNote={false}
          />
        )}
      </Box>
    </>
  );
}
